import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { IProductComplete } from "../interfaces/Products/IProductDetail";
import { useLocation, useNavigate } from "react-router-dom";
import {
  loginGenialWithToken,
  logoutUser,
  renewToken,
} from "../hooks/useAuthService";
import { ISingInUser } from "../interfaces/User";
import { useUser } from "../hooks/useUsers";
import { ERole, INewUser } from "../interfaces/User/newUser";
import { client_selected } from "../client";
import { useInterestStorage } from "../hooks/useInterestStorage";

interface IProps {
  children: React.ReactNode;
}

export interface InterestContext extends IProductComplete {
  selected?: boolean;
}

export interface ISignInWithToken {
  token: string;
  route: string;
}

interface AuthContextType {
  signIn: (data: ISingInUser) => Promise<void>;
  signInWithToken: (data: ISignInWithToken) => Promise<void>;
  logout: () => void;
  cancelRequests: () => void;
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  error: string | null;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
  interest: InterestContext[];
  setInterest: React.Dispatch<React.SetStateAction<InterestContext[]>>;
  returnPath: (path: string) => void;
  user: INewUser;
  isAuthenticated: boolean;
  isTempPassword: boolean;
  roleSelected: ERole | null;
  setRoleSelected: React.Dispatch<React.SetStateAction<ERole | null>>;
  isInMaintenance: boolean;
  accessExternalLink: string | undefined | null;
}

const Context = createContext<AuthContextType>({} as AuthContextType);

export const AuthProvider = ({ children }: IProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const abortControllerRef = useRef<AbortController | null>(null);
  const [user, setUser] = useState<INewUser>({} as INewUser);
  const refreshTokenStorage = localStorage.getItem("refreshToken");
  const accessTokenStorage = localStorage.getItem("accessToken");
  const [roleSelected, setRoleSelected] = useState<ERole | null>(null);
  const queryClient = useQueryClient();
  const isTempPassword =
    !!localStorage.getItem("isTempPassword") &&
    localStorage.getItem("isTempPassword") === "true";
  const isAuthenticated =
    !!localStorage.getItem("isAuthenticated") &&
    localStorage.getItem("isAuthenticated") === "true";
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [interest, setInterest] = useState<InterestContext[]>([]);
  const redirectClient = client_selected.redirect;
  const isAutentication = location.pathname.includes("/autenticacao");
  const isInMaintenance = process.env.REACT_APP_MAINTENANCE === "TRUE";
  const accessExternalLink = process.env.REACT_APP_ACCESS_EXTERNAL;

  const { saveInterestsToStorage, getInterestsFromStorage, mergedInterests } =
    useInterestStorage();

  const { data: dataUser } = useUser({
    enable: !!!user?.id && !!isAuthenticated && !isTempPassword,
  });

  const updateInterests = useCallback(
    (
      value:
        | InterestContext[]
        | ((prevState: InterestContext[]) => InterestContext[])
    ) => {
      setInterest((prevState) => {
        const newInterests =
          typeof value === "function" ? value(prevState) : value;
        if (user?.id) {
          saveInterestsToStorage(user.id, newInterests);
        } else {
          saveInterestsToStorage(null, newInterests);
        }
        return newInterests;
      });
    },
    [user?.id, saveInterestsToStorage]
  );

  const signIn = async (data: ISingInUser) => {
    try {
      const { accessToken, path_to_redirect } = data;

      const { profile } = data.user;
      const role = profile[0].name;
      const payload = accessToken.split(".")[1];
      const decodedPayload = JSON.parse(atob(payload));
      const userId = decodedPayload.sub;

      // Limpa qualquer cache/estado do usuário anterior antes de setar o novo
      queryClient.clear();
      setUser({} as INewUser);
      setRoleSelected(null);
      setInterest([]);

      localStorage.setItem("refreshToken", data.refreshToken);
      localStorage.setItem("accessToken", accessToken);
      localStorage.setItem("isAuthenticated", "true");
      const redirectPath = localStorage.getItem("redirect_path");

      setRoleSelected(role);
      setUser(data.user);

      if (userId) {
        const mergedUserInterests = mergedInterests(userId) || [];
        setInterest(mergedUserInterests);
      }
      setError(null);

      if (data.user.isPasswordExpired) {
        localStorage.setItem("isTempPassword", "true");
        navigate("/autenticacao/mudar-senha-temporaria");
      } else {
        localStorage.removeItem("isTempPassword");
        localStorage.removeItem("redirect_path");
        navigate(redirectPath || path_to_redirect || redirectClient);
      }
    } catch (error) {
      console.error("Erro durante o login:", error);
      setError("Ocorreu um erro durante o login. Tente novamente.");
    }
  };

  const signInWithToken = async ({ token, route }: ISignInWithToken) => {
    setIsLoading(true);

    const redirectPath = `/marketplace/${route}`;
    try {
      const response = await loginGenialWithToken(token);
      if (!!response.data) {
        functionsLogout(true);
        let data = response.data;
        data.path_to_redirect = redirectPath;
        await signIn(data);
      }
      setIsLoading(false);
    } catch (error) {
      console.error("Erro durante o login:", error);
      setError("Ocorreu um erro durante o login. Tente novamente.");
      setIsLoading(false);
    }
  };

  const cancelRequests = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      setError("Login cancelado pelo usuário.");
    }
  };

  const functionsLogout = useCallback(
    (hasToken?: boolean) => {
      console.log("invalidando token");

      queryClient.clear();
      setRoleSelected(null);
      setUser({} as INewUser);
      setInterest([]);
      setError(null);
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("accessToken");
      localStorage.removeItem("isAuthenticated");
      localStorage.removeItem("isTempPassword");

      if (isInMaintenance) {
        navigate("/manutencao-dos-servicos");
      } else if (hasToken) {
        return;
      } else {
        navigate(redirectClient);
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [navigate]
  );

  const mutationLogout = useMutation(async () => {
    const dataMutationLogout = {
      refreshToken: localStorage.getItem("refreshToken"),
      accessToken: localStorage.getItem("accessToken"),
    };
    if (isAutentication) {
      functionsLogout();
    } else {
      try {
        await logoutUser(dataMutationLogout);
      } catch (error) {
        // console.log("error", error);
      } finally {
        functionsLogout();
      }
    }
  });

  const logout = async () => {
    queryClient.clear();
    setRoleSelected(null);
    await mutationLogout.mutateAsync();
  };

  const returnPath = (path: string) => {
    const from = location?.state?.from;
    const pathnameIncludes = !!from?.includes(path);
    const redirectPath = localStorage.getItem("redirect_path");
    localStorage.removeItem("redirect_path");
    if (!!from && pathnameIncludes) {
      navigate(redirectPath || from);
    } else {
      navigate(redirectClient);
    }
  };

  const { mutate: mutateRenewToken } = useMutation(renewToken, {
    onSuccess: (success) => {
      localStorage.setItem("isAuthenticated", "true");
    },
    onError: (error) => {
      console.error(error);
      logout();
    },
  });

  // Fluxo SSO via sessionStorage (webview app Genial).
  // O app injeta o token antes de carregar a webview. Rodamos uma única
  // vez no mount: se houver token, consumimos e disparamos o login.
  useEffect(() => {
    const webviewToken = sessionStorage.getItem("genial_sso_token");
    if (webviewToken) {
      sessionStorage.removeItem("genial_sso_token");
      signInWithToken({ token: webviewToken, route: "predix" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const isPublicRoute =
      location.pathname.includes("/marketplace") ||
      location.pathname.includes("/404") ||
      location.pathname.includes("/termo-de-uso") ||
      location.pathname.includes("/politica-de-privacidade") ||
      location.pathname.includes("/manutencao-dos-servicos");

    if (
      !refreshTokenStorage &&
      !accessTokenStorage &&
      !isAuthenticated &&
      !isTempPassword &&
      !isAutentication &&
      !isPublicRoute
    ) {
      functionsLogout();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, isAutentication]);

  useEffect(() => {
    if (isTempPassword) {
      return;
    }

    if (refreshTokenStorage && accessTokenStorage && !isAuthenticated) {
      mutateRenewToken();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isTempPassword,
    refreshTokenStorage,
    accessTokenStorage,
    isAuthenticated,
  ]);

  useEffect(() => {
    if (dataUser && !user?.id && isAuthenticated) {
      setUser(dataUser);
      //@ts-ignore
      setRoleSelected(dataUser?.profile?.name);
      const userInterests = getInterestsFromStorage(dataUser.id);
      setInterest(userInterests);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataUser, user?.id, isAuthenticated]);

  useEffect(() => {
    if (user?.id) {
      const userInterests = getInterestsFromStorage(user.id);
      setInterest(userInterests);
    } else {
      const guestInterests = getInterestsFromStorage(null);
      setInterest(guestInterests);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  return (
    <Context.Provider
      value={{
        isAuthenticated,
        isTempPassword,
        signIn,
        signInWithToken,
        cancelRequests,
        logout,
        isLoading,
        setIsLoading,
        error,
        setError,
        interest,
        setInterest: updateInterests,
        returnPath,
        user,
        roleSelected,
        setRoleSelected,
        isInMaintenance,
        accessExternalLink,
      }}
    >
      {children}
    </Context.Provider>
  );
};

export function useAuth() {
  const context = useContext(Context);

  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider.");
  }

  return context;
}

export default Context;
